import type { FC, ReactNode } from 'react'

import { ArrowRight } from 'lucide-react'
import { cn, getInitials } from '@/lib/utils'
import Container from '@/components/landing/container'
import SectionHeading from '@/components/landing/section-heading'

import { LEADERSHIP, WHY_FEATURES } from '@/constants/landing'

const ICON_TONES: string[] = [
    'bg-secondary text-primary',
    'bg-accent text-brand-teal-ink',
    'bg-brand-orange/12 text-brand-orange-ink'
]

const AVATAR_TONES: string[] = [
    'bg-brand-orange text-brand-navy',
    'bg-brand-teal-bright text-brand-navy',
    'bg-white text-primary'
]

const WhySection: FC = (): ReactNode => {
    return (
        <section id='why-skillkafe' aria-labelledby='why-title' className='py-20 sm:py-28'>
            <Container>
                <SectionHeading
                    id='why-title'
                    align='center'
                    eyebrow="Why Skillkafe"
                    title="Everything you need to walk into a corporate job with confidence"
                    description="Flexible online learning, live support from experts and real connections to employers, at a price that works for you."
                />

                <div className='mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                    <div className='reveal relative isolate flex flex-col overflow-hidden rounded-[2rem] bg-primary p-8 text-white sm:p-10 md:col-span-2 lg:col-span-1 lg:row-span-3'>
                        <div aria-hidden='true' className='absolute -right-20 -top-20 -z-10 size-64 rounded-full bg-brand-teal/30 blur-2xl' />
                        <div aria-hidden='true' className='absolute -bottom-24 -left-16 -z-10 size-64 rounded-full border-[36px] border-white/5' />

                        <p className='font-heading text-7xl font-bold leading-none tracking-tight sm:text-8xl'>
                            60
                            <span className='text-brand-orange'>+</span>
                        </p>

                        <p className='mt-4 text-xl font-semibold leading-snug'>
                            years of combined leadership experience
                        </p>

                        <p className='mt-4 leading-relaxed text-white/75'>
                            Our leaders bring decades of experience in learning and development, insurance, operations and corporate events, and built Skillkafe around what employers actually look for.
                        </p>

                        <div className='mt-auto flex flex-wrap items-center gap-4 pt-10'>
                            <div aria-hidden='true' className='flex -space-x-3'>
                                {LEADERSHIP.map((leader, index) => (
                                    <span
                                        key={leader.name}
                                        className={cn(
                                            'grid size-11 place-items-center rounded-full font-heading text-sm font-semibold ring-4 ring-primary',
                                            AVATAR_TONES[index % AVATAR_TONES.length]
                                        )}
                                    >
                                        {getInitials(leader.name)}
                                    </span>
                                ))}
                            </div>

                            <a href='#team' className='group inline-flex items-center gap-1.5 text-sm font-semibold text-white underline-offset-4 hover:underline'>
                                Meet the team
                                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                            </a>
                        </div>
                    </div>

                    {WHY_FEATURES.map((feature, index) => {
                        const Icon = feature.icon

                        return (
                            <div
                                key={feature.title}
                                className='reveal rounded-[1.75rem] border border-border bg-card p-7 transition-[border-color,box-shadow] duration-300 hover:border-primary/25 hover:shadow-[0_24px_50px_-30px_rgba(16,22,90,0.4)]'
                            >
                                <span className={cn('grid size-12 place-items-center rounded-2xl', ICON_TONES[index % ICON_TONES.length])}>
                                    <Icon className='size-6' aria-hidden='true' />
                                </span>

                                <h3 className='mt-5 text-lg font-semibold text-foreground'>
                                    {feature.title}
                                </h3>

                                <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                    {feature.description}
                                </p>
                            </div>
                        )
                    })}
                </div>
            </Container>
        </section>
    )
}

export default WhySection
